<script setup>
import { $api, getApiBaseUrl } from "@/utils/api";

definePage({
  meta: {
    requiresAuth: true,
  },
});

const clientes = ref([]);
const loading = ref(false);
const search = ref("");

// Cliente expandido para ver projetos
const clienteExpandido = ref(null);
const projetosCliente = ref([]);
const loadingProjetos = ref(false);

// Dialog Novo Projeto
const dialogProjeto = ref(false);
const projetoEnviando = ref(false);
const projetoErro = ref("");
const clientesOptions = ref([]);
const clientesCarregando = ref(false);

const formProjeto = ref({
  numero_projeto: "",
  nome_projeto: "",
  cliente_id: null,
  data_inicial: "",
  data_final: "",
  previsao_termino: "",
  prazo_final: "",
  local_cidade: "",
  dados_contratuais: "",
});

const erroClientes = ref("");

async function fetchClientes() {
  loading.value = true;
  erroClientes.value = "";
  try {
    const params = new URLSearchParams();
    if (search.value.trim()) params.set("search", search.value.trim());
    const data = await $api(`/rdo/clientes-projetos?${params}`);
    if (data.error) {
      erroClientes.value = data.error;
      clientes.value = [];
    } else {
      clientes.value = Array.isArray(data) ? data : [];
    }
  } catch (e) {
    console.error(e);
    erroClientes.value =
      e?.data?.error || e?.message || "Erro ao carregar clientes";
    clientes.value = [];
  } finally {
    loading.value = false;
  }
}

async function expandirCliente(cliente) {
  if (clienteExpandido.value === cliente.id) {
    clienteExpandido.value = null;
    projetosCliente.value = [];
    return;
  }

  clienteExpandido.value = cliente.id;
  loadingProjetos.value = true;
  try {
    const data = await $api(`/rdo/clientes/${cliente.id}/projetos`);
    projetosCliente.value = data?.projetos ?? (Array.isArray(data) ? data : []);
  } catch (e) {
    console.error(e);
    projetosCliente.value = [];
  } finally {
    loadingProjetos.value = false;
  }
}

watch(search, () => {
  fetchClientes();
});

async function abrirDialogProjeto() {
  dialogProjeto.value = true;
  projetoErro.value = "";
  formProjeto.value = {
    numero_projeto: "",
    nome_projeto: "",
    cliente_id: null,
    data_inicial: "",
    data_final: "",
    previsao_termino: "",
    prazo_final: "",
    local_cidade: "",
    dados_contratuais: "",
  };
  clientesCarregando.value = true;
  try {
    const data = await $api("/clientes");
    clientesOptions.value = Array.isArray(data) ? data : data.data ?? [];
  } catch (e) {
    console.error(e);
    clientesOptions.value = [];
  } finally {
    clientesCarregando.value = false;
  }
}

function fecharDialogProjeto() {
  dialogProjeto.value = false;
}

watch(
  () => formProjeto.value.data_inicial,
  (nova) => {
    if (
      nova &&
      formProjeto.value.data_final &&
      nova > formProjeto.value.data_final
    ) {
      formProjeto.value.data_final = "";
    }
  },
);
watch(
  () => formProjeto.value.data_final,
  (nova) => {
    if (
      nova &&
      formProjeto.value.data_inicial &&
      formProjeto.value.data_inicial > nova
    ) {
      formProjeto.value.data_inicial = "";
    }
  },
);

async function salvarProjeto() {
  if (!formProjeto.value.numero_projeto?.trim()) {
    projetoErro.value = "Informe o número do projeto.";
    return;
  }
  if (!formProjeto.value.nome_projeto?.trim()) {
    projetoErro.value = "Informe o nome do projeto.";
    return;
  }
  if (!formProjeto.value.cliente_id) {
    projetoErro.value = "Selecione o cliente.";
    return;
  }
  if (!formProjeto.value.data_inicial) {
    projetoErro.value = "Informe a data inicial.";
    return;
  }

  projetoErro.value = "";
  projetoEnviando.value = true;
  try {
    await $api("/projetos", {
      method: "POST",
      body: {
        numero_projeto: formProjeto.value.numero_projeto.trim(),
        nome_projeto: formProjeto.value.nome_projeto.trim(),
        cliente_id: formProjeto.value.cliente_id,
        data_inicial: formProjeto.value.data_inicial,
        data_final: formProjeto.value.data_final || null,
        previsao_termino: formProjeto.value.previsao_termino || null,
        prazo_final: formProjeto.value.prazo_final || null,
        local_cidade: formProjeto.value.local_cidade?.trim() || null,
        dados_contratuais: formProjeto.value.dados_contratuais?.trim() || null,
      },
    });
    fecharDialogProjeto();
    fetchClientes();
    if (clienteExpandido.value === formProjeto.value.cliente_id) {
      expandirCliente({ id: formProjeto.value.cliente_id });
    }
  } catch (e) {
    console.error(e);
    const data = e?.data || e?.response?.data || {};
    const msg = data?.message;
    const errs = data?.errors;
    const lista = errs ? Object.values(errs).flat().filter(Boolean) : [];
    projetoErro.value =
      msg || (lista.length ? lista.join(" ") : "Erro ao salvar projeto.");
  } finally {
    projetoEnviando.value = false;
  }
}

function formatarData(data) {
  if (!data) return "—";
  return new Date(data).toLocaleDateString("pt-BR");
}

function imprimirRdoProjeto(projetoId) {
  const dataAtual = new Date().toISOString().split("T")[0];
  const apiBase = getApiBaseUrl();
  const url = `${apiBase}/rdo/${projetoId}/pdf?data=${dataAtual}`;
  window.open(url, "_blank");
}

onMounted(() => fetchClientes());
</script>

<template>
  <div>
    <VRow>
      <VCol cols="12">
        <VCard>
          <VCardTitle class="text-h5 pa-4 d-flex align-center">
            <span>RDO - Relatório Diário de Obras</span>
            <VSpacer />
            <VBtn color="primary" @click="abrirDialogProjeto">
              <VIcon icon="tabler-plus" start />
              Novo Projeto
            </VBtn>
          </VCardTitle>
          <VDivider />
          <VCardText class="pa-4">
            <!-- Filtros -->
            <div class="d-flex flex-wrap align-end gap-3 mb-4">
              <VTextField
                v-model="search"
                label="Buscar Cliente"
                placeholder="Nome do cliente"
                density="compact"
                hide-details
                clearable
                class="flex-grow-1 flex-md-grow-0"
                style="min-width: 200px; max-width: 320px"
                prepend-inner-icon="tabler-search"
                variant="outlined" />
            </div>

            <!-- Tabela de Clientes -->
            <VProgressLinear v-if="loading" indeterminate class="mb-4" />

            <VTable
              v-if="!loading && clientes.length > 0"
              density="comfortable">
              <thead>
                <tr>
                  <th width="50"></th>
                  <th>Cliente</th>
                  <th width="150" class="text-center">Qtd. Projetos</th>
                  <th width="100">Ações</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="cliente in clientes" :key="cliente.id">
                  <tr
                    class="cursor-pointer"
                    :class="{
                      'bg-primary-lighten-5': clienteExpandido === cliente.id,
                    }"
                    @click="expandirCliente(cliente)">
                    <td>
                      <VIcon
                        :icon="
                          clienteExpandido === cliente.id
                            ? 'tabler-chevron-down'
                            : 'tabler-chevron-right'
                        "
                        size="20" />
                    </td>
                    <td>
                      <div class="d-flex align-center gap-2">
                        <VIcon
                          icon="tabler-building"
                          size="20"
                          color="primary" />
                        <span class="font-weight-medium">{{
                          cliente.nome
                        }}</span>
                      </div>
                    </td>
                    <td class="text-center">
                      <VChip color="primary" size="small" variant="tonal">
                        {{ cliente.total_projetos }}
                        {{
                          cliente.total_projetos === 1 ? "projeto" : "projetos"
                        }}
                      </VChip>
                    </td>
                    <td @click.stop>
                      <VTooltip text="Detalhamento do Cliente" location="top">
                        <template #activator="{ props: tProps }">
                          <VBtn
                            v-bind="tProps"
                            icon
                            variant="text"
                            size="small"
                            color="primary"
                            :to="{
                              name: 'rdo-cliente-id',
                              params: { id: cliente.id },
                            }">
                            <VIcon icon="tabler-search" />
                          </VBtn>
                        </template>
                      </VTooltip>
                    </td>
                  </tr>
                  <!-- Projetos do cliente expandido -->
                  <tr v-if="clienteExpandido === cliente.id">
                    <td colspan="4" class="pa-0">
                      <VExpandTransition>
                        <div class="bg-grey-lighten-5 pa-4">
                          <VProgressLinear
                            v-if="loadingProjetos"
                            indeterminate
                            class="mb-3" />
                          <template v-else-if="projetosCliente.length > 0">
                            <p class="text-subtitle-2 mb-3">
                              Projetos de {{ cliente.nome }}:
                            </p>
                            <VTable density="compact" class="bg-white rounded">
                              <thead>
                                <tr>
                                  <th>Nº Projeto</th>
                                  <th>Nome do Projeto</th>
                                  <th>Data Inicial</th>
                                  <th>Data Final</th>
                                  <th width="100">Ações</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr
                                  v-for="projeto in projetosCliente"
                                  :key="projeto.id">
                                  <td>{{ projeto.numero_projeto }}</td>
                                  <td>{{ projeto.nome }}</td>
                                  <td>
                                    {{ formatarData(projeto.dat - inicial) }}
                                  </td>
                                  <td>
                                    {{ formatarData(projeto.data_final) }}
                                  </td>
                                  <td>
                                    <VTooltip text="Abrir RDO" location="top">
                                      <template #activator="{ props: tProps }">
                                        <VBtn
                                          v-bind="tProps"
                                          icon
                                          variant="text"
                                          size="small"
                                          color="primary"
                                          :to="{
                                            name: 'rdo-id',
                                            params: { id: projeto.id },
                                          }">
                                          <VIcon icon="tabler-search" />
                                        </VBtn>
                                      </template>
                                    </VTooltip>
                                    <VTooltip
                                      text="Imprimir RDO"
                                      location="top">
                                      <template #activator="{ props: tProps }">
                                        <VBtn
                                          v-bind="tProps"
                                          icon
                                          variant="text"
                                          size="small"
                                          color="info"
                                          @click.stop="
                                            imprimirRdoProjeto(projeto.id)
                                          ">
                                          <VIcon icon="tabler-printer" />
                                        </VBtn>
                                      </template>
                                    </VTooltip>
                                  </td>
                                </tr>
                              </tbody>
                            </VTable>
                          </template>
                          <p v-else class="text-body-2 text-medium-emphasis">
                            Nenhum projeto cadastrado para este cliente.
                          </p>
                        </div>
                      </VExpandTransition>
                    </td>
                  </tr>
                </template>
              </tbody>
            </VTable>

            <VAlert v-else-if="!loading" type="info" variant="tonal">
              Nenhum cliente encontrado.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Dialog Novo Projeto -->
    <VDialog
      v-model="dialogProjeto"
      max-width="600"
      persistent
      @click:outside="fecharDialogProjeto">
      <VCard>
        <VCardTitle class="d-flex align-center">
          <VIcon icon="tabler-folder-plus" class="me-2" />
          Novo Projeto
        </VCardTitle>
        <VDivider />
        <VCardText>
          <VForm @submit.prevent="salvarProjeto">
            <VRow>
              <VCol cols="12" md="6">
                <VTextField
                  v-model="formProjeto.numero_projeto"
                  label="Número do Projeto *"
                  placeholder="Ex.: 2024/001"
                  density="compact"
                  variant="outlined" />
              </VCol>
              <VCol cols="12" md="6">
                <VTextField
                  v-model="formProjeto.nome_projeto"
                  label="Nome do Projeto *"
                  placeholder="Nome do projeto"
                  density="compact"
                  variant="outlined" />
              </VCol>
              <VCol cols="12">
                <VAutocomplete
                  v-model="formProjeto.cliente_id"
                  :items="clientesOptions"
                  item-title="nome"
                  item-value="id"
                  label="Cliente *"
                  placeholder="Selecione o cliente"
                  density="compact"
                  variant="outlined"
                  :loading="clientesCarregando"
                  clearable />
              </VCol>
              <VCol cols="12" md="6">
                <VTextField
                  v-model="formProjeto.data_inicial"
                  label="Data Inicial *"
                  type="date"
                  :max="formProjeto.data_final || undefined"
                  density="compact"
                  variant="outlined" />
              </VCol>
              <VCol cols="12" md="6">
                <VTextField
                  v-model="formProjeto.data_final"
                  label="Data Final"
                  type="date"
                  :min="formProjeto.dat - inicial || undefined"
                  density="compact"
                  variant="outlined" />
              </VCol>
              <VCol cols="12" md="6">
                <VTextField
                  v-model="formProjeto.previsao_termino"
                  label="Previsão Término"
                  type="date"
                  density="compact"
                  variant="outlined" />
              </VCol>
              <VCol cols="12" md="6">
                <VTextField
                  v-model="formProjeto.prazo_final"
                  label="Prazo Final"
                  type="date"
                  density="compact"
                  variant="outlined" />
              </VCol>
              <VCol cols="12">
                <VTextField
                  v-model="formProjeto.local_cidade"
                  label="Local / Cidade"
                  placeholder="Local ou cidade do projeto"
                  density="compact"
                  variant="outlined" />
              </VCol>
              <VCol cols="12">
                <VTextarea
                  v-model="formProjeto.dados_contratuais"
                  label="Dados Contratuais"
                  placeholder="Informações contratuais do projeto"
                  density="compact"
                  variant="outlined"
                  rows="3" />
              </VCol>
            </VRow>

            <VAlert
              v-if="projetoErro"
              type="error"
              density="compact"
              class="mb-3 mt-2"
              closable>
              {{ projetoErro }}
            </VAlert>

            <div class="d-flex justify-end gap-2 mt-4">
              <VBtn variant="outlined" @click="fecharDialogProjeto">
                Cancelar
              </VBtn>
              <VBtn
                type="submit"
                color="primary"
                :loading="projetoEnviando"
                :disabled="projetoEnviando">
                Salvar Projeto
              </VBtn>
            </div>
          </VForm>
        </VCardText>
      </VCard>
    </VDialog>
  </div>
</template>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
.cursor-pointer:hover {
  background-color: rgba(var(--v-theme-primary), 0.04);
}
.bg-primary-lighten-5 {
  background-color: rgba(var(--v-theme-primary), 0.08) !important;
}
</style>
